import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type { Future } from '@warp-drive/core/request';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Transition from '@ember/routing/transition';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { loadBetaHomeModel } from 'tio-employee/utils/beta/load-home-model';

export type DashboardRouteModel = ReturnType<AuthenticatedDashboardRoute['model']>;

/** The in-flight `/people/:id` request the loan-summary widget resolves for itself. */
export type PersonRequest = Future<{ data: PersonModel }>;

/**
 * Re-exported from its new home in `utils/beta/load-home-model` so the many
 * existing importers of this path keep working. `/beta/home-preview` renders
 * the same page full-bleed and loads it from there too.
 */
export type { BetaHomeRouteModel } from 'tio-employee/utils/beta/load-home-model';

export default class AuthenticatedDashboardRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare reimaginedData: ReimaginedDataService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  model() {
    // Refetch the SLR widget's server-driven state on each dashboard entry —
    // its trackedFunction inputs never change after login, so without this a
    // resolved (or newly arisen) alert would keep the tile's stale state for
    // the whole SPA session. All three tile inputs, not just alerts: the
    // partially-applied and loan-standing reads go stale the same way and feed
    // the same `hasError`.
    this.employee.refreshSlrState();

    const personId = this.sessionContext.user.person.id!;

    // Only the loan-summary widget reads this, so the request is handed to the
    // template unresolved: the grid paints while it is in flight instead of the
    // whole route waiting on it. Other employee/company data is already loaded
    // via the /me endpoint in session-context.
    const personRequest = this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'accounts.loans,accounts.postal-address',
        reload: true,
      })
    ) as unknown as PersonRequest;

    // isTaOnly uses employees.plan which is already loaded via /me endpoint
    const employees = this.sessionContext.user.employees;
    const isTaOnlyCompany = this.partner.isTaOnly;
    const companyKey = employees.map((e) => e.plan?.companyKey);
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId;
    const isTaOnlyEmployee = taOnlyPlanId ? companyKey.includes(taOnlyPlanId) : false;

    const authenticatedModel = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
    };

    const model = {
      personRequest,
      isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
      wellnessData: authenticatedModel.wellnessData,
    };

    // Same treatment for the beta home data: the template owns its pending state,
    // so the router does not wait on the eight beta fetches either.
    if (this.betaExperience.isEnabled) {
      return { ...model, beta: loadBetaHomeModel(this.reimaginedData) };
    }

    return model;
  }
}
